import { Injectable, InjectionToken, Provider, Signal, computed, signal } from '@angular/core';
import {
	DEFAULT_TENANT_FEATURES,
	TENANT_FEATURE_OVERRIDES,
	TenantFeatures,
	normalizeTenantKey,
} from '../branding-config/tenant-features';

export interface TenantFeatureProvider {
	tenant: Signal<string | null>;
	features: Signal<TenantFeatures>;
	additionalAssignmentEnabled: Signal<boolean>;
	specializationEnabled: Signal<boolean>;
	caseGroupEnabled: Signal<boolean>;
	legalBaseRequiredInP2Enabled: Signal<boolean>;
	careRelatedDisabilityTypeRequiredInP2Enabled: Signal<boolean>;
	additionalReportsEnabled: Signal<boolean>;
	dashboardNetworkContactOverviewVisible: Signal<boolean>;
	setTenant(tenantName: string): void;
}

export const TENANT_FEATURES = new InjectionToken<TenantFeatureProvider>('TENANT_FEATURES');

@Injectable({
	providedIn: 'root',
})
export class TenantFeatureService implements TenantFeatureProvider {
	public readonly tenant = signal<string | null>(null);

	public readonly features = computed(() => ({
		...DEFAULT_TENANT_FEATURES,
		...(TENANT_FEATURE_OVERRIDES[this.tenant() ?? ''] ?? {}),
	}));

	public readonly additionalAssignmentEnabled = computed(() => this.features().additionalAssignment);
	public readonly specializationEnabled = computed(() => this.features().specialization);
	public readonly caseGroupEnabled = computed(() => this.features().caseGroup);
	public readonly legalBaseRequiredInP2Enabled = computed(() => this.features().legalBaseRequiredInP2);
	public readonly careRelatedDisabilityTypeRequiredInP2Enabled = computed(
		() => this.features().careRelatedDisabilityTypeRequiredInP2,
	);
	public readonly additionalReportsEnabled = computed(() => this.features().additionalReports);
	public readonly dashboardNetworkContactOverviewVisible = computed(
		() => this.features().dashboardNetworkContactOverviewVisible,
	);

	public setTenant(tenantName: string): void {
		this.tenant.set(normalizeTenantKey(tenantName));
	}
}

export const provideTenantFeatures: Provider = {
	provide: TENANT_FEATURES,
	useClass: TenantFeatureService,
};
